/**
 * Widget filter language (spec §9.7): a JSON AST evaluated client-side here and server-side in
 * `core/filter.py`. Both must agree; `tests/vectors/structure.json` (filters), `signals.json` and
 * `filter_dates.json` pin the semantics.
 *
 *   {"and":[…]} {"or":[…]} {"not":…}
 *   {"group":"home"} {"type":"recurring"} {"tag":"propId"} {"parent":"taskId"} {"seq":"seqId"}
 *   {"prop":"energy","lt":3}  ops: eq neq gt lt gte lte in has
 *   {"text":"needle"} {"done":true|false} {"archived":true|false}
 *   {"signal":"weather.rain_next_3h","eq":false}  — outside facts (ADR 0016); unknown never hides
 *   {"prop":"due","before":{"days":7}}  date ops (ADR 0020): before after on onOrBefore onOrAfter between;
 *       a day is "YYYY-MM-DD" or {"days":n} from today; a date-time counts as its day in the time zone
 */
import { matchesText } from "./textMatch";
import type { Task } from "./schema";
import { signalNode, type SignalAst, type Signals } from "./signals";

/** A day in a filter: "YYYY-MM-DD", or `{days: n}` counted from today (0 today, -1 yesterday). */
export type DayRef = string | { days: number };
export const DATE_OPS = ["before", "after", "on", "onOrBefore", "onOrAfter", "between"] as const;
export type DateOp = (typeof DATE_OPS)[number];

export type FilterAst =
  | { and: FilterAst[] }
  | { or: FilterAst[] }
  | { not: FilterAst }
  | { group: string }
  | { type: string }
  | { tag: string }
  | { parent: string | null }
  | { seq: string }
  | { text: string }
  | { done: boolean }
  | { archived: boolean }
  | ({ prop: string } & Partial<Record<"eq" | "neq" | "gt" | "lt" | "gte" | "lte", unknown>> & {
        in?: unknown[];
        has?: unknown;
      } & Partial<Record<"before" | "after" | "on" | "onOrBefore" | "onOrAfter", DayRef>> & {
        between?: [DayRef, DayRef];
      })
  | ({ signal: string } & Partial<Record<"eq" | "neq" | "gt" | "lt" | "gte" | "lte" | "has", unknown>> & {
        in?: unknown[];
      })
  | Record<string, never>;

export interface FilterCtx {
  /** computed values available as props too (want, blocked, …): resolved lazily per task */
  computed?: (task: Task, key: string) => unknown;
  groupsOf?: (taskId: string) => string[];
  seqsOf?: (taskId: string) => string[];
  parentOf?: (task: Task) => string | null;
  /** effective signal values (ADR 0016); absent: every signal is unknown */
  signals?: Signals | null;
  /** what date comparisons count from (ADR 0020): today as "YYYY-MM-DD"; absent, the device's today */
  today?: string;
  /** the zone a date-time's day is read in; absent, the device's */
  timeZone?: string;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** "YYYY-MM-DD" of a moment in a time zone (the device's when none is given). */
export function dayIn(ms: number, timeZone?: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(ms));
}

function addDaysTo(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function dayOf(v: unknown, timeZone?: string): string | null {
  if (typeof v !== "string") return null;
  if (DAY.test(v)) return v;
  const ms = Date.parse(v);
  return Number.isNaN(ms) ? null : dayIn(ms, timeZone);
}

function refOf(x: unknown, today: string): string | null {
  if (x && typeof x === "object" && typeof (x as { days?: unknown }).days === "number")
    return addDaysTo(today, Math.trunc((x as { days: number }).days));
  return typeof x === "string" && DAY.test(x) ? x : null;
}

function dateTest(v: unknown, op: DateOp, arg: unknown, ctx: FilterCtx): boolean {
  const day = dayOf(v, ctx.timeZone);
  if (day === null) return false;
  const today = ctx.today ?? dayIn(Date.now(), ctx.timeZone);
  if (op === "between") {
    if (!Array.isArray(arg) || arg.length !== 2) return false;
    const lo = refOf(arg[0], today);
    const hi = refOf(arg[1], today);
    return lo !== null && hi !== null && lo <= day && day <= hi;
  }
  const ref = refOf(arg, today);
  if (ref === null) return false;
  // "YYYY-MM-DD" strings compare as days
  return op === "before"
    ? day < ref
    : op === "after"
      ? day > ref
      : op === "on"
        ? day === ref
        : op === "onOrBefore"
          ? day <= ref
          : day >= ref;
}

/** True unless the filter is definitely false: an unknown signal never hides anything (ADR 0016). */
export function matches(task: Task, ast: FilterAst | undefined | null, ctx: FilterCtx = {}): boolean {
  return evaluate(task, ast, ctx) !== false;
}

/** Three-valued: true, false, or null when it depends on a signal that is unknown (Kleene and/or/not). */
export function evaluate(task: Task, ast: FilterAst | undefined | null, ctx: FilterCtx = {}): boolean | null {
  if (!ast || Object.keys(ast).length === 0) return true;
  if ("and" in ast) {
    const parts = ast.and.map((a) => evaluate(task, a, ctx));
    return parts.includes(false) ? false : parts.includes(null) ? null : true;
  }
  if ("or" in ast) {
    const parts = ast.or.map((a) => evaluate(task, a, ctx));
    return parts.includes(true) ? true : parts.includes(null) ? null : false;
  }
  if ("not" in ast) {
    const r = evaluate(task, ast.not, ctx);
    return r === null ? null : !r;
  }
  if ("signal" in ast) return signalNode(ast as SignalAst, ctx.signals);
  if ("group" in ast) return (ctx.groupsOf?.(task.id) ?? []).includes(ast.group);
  if ("type" in ast) return task.typeId === ast.type;
  if ("tag" in ast) return task.props[ast.tag] === true;
  if ("parent" in ast) return (ctx.parentOf?.(task) ?? null) === ast.parent;
  if ("seq" in ast) return (ctx.seqsOf?.(task.id) ?? []).includes(ast.seq);
  if ("text" in ast) return matchesText([task.title, task.notes], ast.text);
  if ("done" in ast) return (task.doneAt !== null) === ast.done;
  if ("archived" in ast) return (task.archivedAt !== null) === ast.archived;
  if ("prop" in ast) {
    const key = ast.prop;
    const v = key in task.props ? task.props[key] : ctx.computed?.(task, key);
    for (const op of DATE_OPS)
      if (op in ast) return dateTest(v, op, (ast as Record<string, unknown>)[op], ctx);
    if ("eq" in ast) return v === ast.eq;
    if ("neq" in ast) return v !== ast.neq;
    if ("gt" in ast) return typeof v === "number" && v > Number(ast.gt);
    if ("lt" in ast) return typeof v === "number" && v < Number(ast.lt);
    if ("gte" in ast) return typeof v === "number" && v >= Number(ast.gte);
    if ("lte" in ast) return typeof v === "number" && v <= Number(ast.lte);
    if ("in" in ast && ast.in)
      return Array.isArray(v) ? v.some((x) => ast.in!.includes(x)) : ast.in.includes(v);
    if ("has" in ast) return Array.isArray(v) ? v.includes(ast.has) : v === ast.has;
    return v !== undefined && v !== null && v !== false;
  }
  return true;
}
